import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldAlert, Gavel, Mail, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function TermsOfUsePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useDocumentMeta({
    title: "Terms of Use | TeleMedCare - Telehealth Terms & Disclaimers",
    description: "Read the Terms of Use for TeleMedCare. Understand the nature of virtual consultations, emergency disclaimers, and AI tool supervision.",
    canonical: "https://telemedcare.app/terms",
  });

  const sections = [
    { id: "agreement", title: "1. Agreement & Eligibility" },
    { id: "platform-role", title: "2. Telehealth Platform Role" },
    { id: "emergency", title: "3. EMERGENCY DISCLAIMER" },
    { id: "ai-scribe", title: "4. Role of AI Care Tools" },
    { id: "accounts", title: "5. Patient Portals & Accounts" },
    { id: "payments", title: "6. Payment & Subscriptions" },
    { id: "conduct", title: "7. Prohibited Code of Conduct" },
    { id: "liability", title: "8. Limitation of Liability" },
    { id: "governing", title: "9. Governing Law" },
  ];

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Navbar />

      <main className="pt-[72px]">
        {/* Header Hero Section */}
        <section className="bg-ink text-cream py-16 lg:py-24 relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-clay/20 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 left-0 h-[300px] w-[300px] rounded-full bg-forest/25 blur-3xl"
          />
          <div className="container-x relative">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-clay/15 border border-clay/30 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                <Gavel className="h-3.5 w-3.5" />
                Legal Agreement
              </span>
              <h1 className="mt-5 font-display text-[40px] sm:text-[52px] font-extrabold leading-[1.1] tracking-tight">
                Terms of Use
              </h1>
              <p className="mt-4 text-[16px] sm:text-[18px] text-cream/80 leading-relaxed">
                Last updated: June 9, 2026. Please read these terms carefully before accessing our virtual care platform or consulting our clinical network.
              </p>
            </div>
          </div>
        </section>

        {/* Core Layout - Sidebar + Content */}
        <section className="py-16 lg:py-24">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Sticky Sidebar Navigation */}
              <nav className="lg:col-span-4 sticky top-28 hidden lg:block bg-white border border-border/40 rounded-3xl p-6 shadow-[0_8px_32px_rgba(14,35,51,0.02)]">
                <p className="text-[12px] uppercase tracking-[0.18em] text-ink/50 font-semibold mb-4">
                  Table of Contents
                </p>
                <ul className="space-y-3.5">
                  {sections.map((sec) => (
                    <li key={sec.id}>
                      <a
                        href={`#${sec.id}`}
                        className="block text-[14.5px] text-ink/70 hover:text-forest transition-colors font-medium border-l-2 border-transparent hover:border-forest/40 pl-3 leading-snug"
                      >
                        {sec.title}
                      </a>
                    </li>
                  ))}
                </ul>

                <hr className="my-6 border-border/60" />

                <div className="rounded-2xl bg-cream/70 p-4 border border-border/40">
                  <div className="flex gap-2 text-clay text-[13px] font-bold uppercase tracking-wider items-center">
                    <ShieldAlert className="h-4 w-4" />
                    Crucial Warning
                  </div>
                  <p className="mt-2 text-[12.5px] text-ink/65 leading-relaxed">
                    This platform is never to be used for acute medical emergencies. Learn more in Section 3.
                  </p>
                </div>
              </nav>

              {/* Main Terms Content */}
              <div className="lg:col-span-8 max-w-3xl">
                
                {/* EMERGENCY CALLOUT CARD */}
                <div className="mb-12 rounded-3xl bg-clay text-cream p-7 lg:p-9 shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <AlertTriangle className="h-32 w-32" />
                  </div>
                  <div className="flex gap-3 items-center text-[12px] uppercase tracking-[0.2em] font-semibold text-cream/90">
                    <ShieldAlert className="h-5 w-5" />
                    Medical Emergency Warning
                  </div>
                  <h2 className="mt-3 font-display text-[22px] sm:text-[24px] font-extrabold tracking-tight mb-4 text-balance">
                    DO NOT USE TELEMEDCARE FOR LIFE-THREATENING EMERGENCIES
                  </h2>
                  <p className="text-[15.5px] text-cream/95 leading-relaxed">
                    If you are experiencing chest pain, severe shortness of breath, heavy bleeding, sudden weakness or numbness, or any other acute, life-threatening symptoms, immediately proceed to the nearest physical hospital emergency room or contact emergency ambulance services.
                  </p>
                </div>

                {/* Section Content */}
                <article className="prose prose-slate max-w-none">
                  
                  {/* Agreement & Eligibility */}
                  <section id="agreement" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      1. Agreement & Eligibility
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Welcome to TeleMedCare. These Terms of Use govern your access and use of the services owned and operated by Hivebrain LTD (“Hivebrain”, “TeleMedCare”, “we”, “our”, or “us”), including our website (telemedcare.app), user portal, and our clinician-matching WhatsApp care contact (+234 810 544 4731).
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      By signing up, initiating a chat, or accessing the services, you agree to comply with and be bound by these Terms of Use and our Privacy Policy.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      To register or use our telehealth matching service, you must be at least 18 years of age and possess the legal authority to enter into a binding contract. If you are registering on behalf of a dependent (such as a minor child), you represent that you are the legal parent or guardian and agree to supervise their consultation at all times.
                    </p>
                  </section>

                  {/* Telehealth Platform Role */}
                  <section id="platform-role" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      2. Telehealth Platform Role
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      TeleMedCare provides technological administrative infrastructure to connect users (patients) with independently licensed clinical professionals (doctors, nurses, therapists).
                    </p>
                    
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li><strong>Independent Practice:</strong> All clinicians offering services via our platform are independent practitioners. They are solely responsible for their diagnosis, prescription choices, care plans, and advice.</li>
                      <li><strong>No Clinical Relationship:</strong> Hivebrain LTD does not practice medicine. The technological matching does not establish a clinical doctor-patient relationship between Hivebrain LTD and you.</li>
                      <li><strong>Care Standard:</strong> We verify the license credentials, medical council standings, and identity of all clinicians before admitting them into our provider network.</li>
                    </ul>
                  </section>

                  {/* EMERGENCY DISCLAIMER */}
                  <section id="emergency" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-clay tracking-tight mb-4 border-b border-clay/35 pb-2">
                      3. EMERGENCY DISCLAIMER
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Telehealth is a valuable, convenient tool for low-acuity conditions, check-ups, ongoing prescription refills, and medical guidance. However, <strong>it is not a replacement for emergency clinical intervention.</strong>
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      By agreeing to these terms, you understand that our doctors cannot run emergency procedures online, and we cannot guarantee instant responses during an acute crisis.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed">
                      If our clinicians determine during a WhatsApp chat or video call that your symptoms indicate a high-risk situation, they will refer you to go to the nearest physical hospital immediately. You agree to act on such referrals without delay.
                    </p>
                  </section>

                  {/* Role of AI Care Tools */}
                  <section id="ai-scribe" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      4. Role of AI Care Tools
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      TeleMedCare leverages clinician-supervised artificial intelligence (AI) models to streamline administrative workflows and aid in diagnostic checklists:
                    </p>
                    
                    <h3 className="font-display text-[17px] font-bold text-ink mb-2">A. AI Symptom Triage</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      Our interactive WhatsApp symptom checker may ask automated clarifying questions before matching you with a physician. These questions help format your intake log. They do not constitute an official diagnosis.
                    </p>

                    <h3 className="font-display text-[17px] font-bold text-ink mb-2">B. AI Scribe Summaries</h3>
                    <p className="text-[15px] text-ink/70 leading-relaxed mb-4">
                      AI systems process consultation transcripts to draft structured clinical summaries for patient files. <strong>A licensed human physician reviews, corrects, and digitally signs every single care plan, prescription, and summary record.</strong>
                    </p>
                  </section>

                  {/* Patient Portals & Accounts */}
                  <section id="accounts" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      5. Patient Portals & Accounts
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      To access prescriptions, lab results, and continuous care timelines, you must create an account on our patient portal.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li><strong>Account Accuracy:</strong> You agree to provide true, current, and complete health profiles. Misrepresenting symptoms or identity can lead to medical mistakes and constitutes a breach of these terms.</li>
                      <li><strong>Security:</strong> You are responsible for maintaining the confidentiality of your login codes and portal access. Notify us immediately if you suspect unauthorized access.</li>
                    </ul>
                  </section>

                  {/* Payment & Subscriptions */}
                  <section id="payments" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      6. Payment & Subscriptions
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      Pricing for consultation bookings, monthly memberships, and family plans are displayed transparently before payment.
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li><strong>Billing:</strong> Fees are billed in Nigerian Naira (NGN) via third-party processors. Subscriptions are set to auto-renew unless canceled before the billing date.</li>
                      <li><strong>Prescription/Lab Fees:</strong> Medications, delivery charges, and laboratory tests are billed separately from consultation fees.</li>
                      <li><strong>Refund Policy:</strong> If a scheduled doctor consultation is canceled by the clinic network, or fails due to a technological breakdown on our end, you are entitled to a full refund or session credit. Standard patient cancellation policies apply.</li>
                    </ul>
                  </section>

                  {/* Prohibited Code of Conduct */}
                  <section id="conduct" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      7. Prohibited Code of Conduct
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      We support our clinical partners in maintaining a safe, professional, and respectful environment. The following acts are strictly prohibited and will result in immediate service termination:
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li>Sending abusive, threatening, obscene, or harassing messages, audio notes, or images to doctors or support representatives.</li>
                      <li>Attempting to obtain controlled pharmaceutical drugs through fraudulent statements or fake medical records.</li>
                      <li>Impersonating any other person or medical professional.</li>
                    </ul>
                  </section>

                  {/* Limitation of Liability */}
                  <section id="liability" className="scroll-mt-24 mb-10">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      8. Limitation of Liability
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, HIVEBRAIN LTD, ITS OFFICERS, DIRECTORS, AND EMPLOYEES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH:
                    </p>
                    <ul className="list-disc pl-5 mb-4 text-[15px] text-ink/70 space-y-2">
                      <li>YOUR USE OF OR INABILITY TO USE THE PLATFORM.</li>
                      <li>ANY CLINICAL TREATMENT, DIAGNOSIS, OR PREVENTATIVE RECOMMENDATIONS PROVIDED BY INDEPENDENT NETWORK PRACTITIONERS.</li>
                      <li>ANY TEMPORARY UNAVAILABILITY OR SLOWDOWNS OF THE WHATSAPP MESSAGE ROUTING NETWORK.</li>
                    </ul>
                  </section>

                  {/* Governing Law */}
                  <section id="governing" className="scroll-mt-24 mb-6">
                    <h2 className="font-display text-[24px] font-extrabold text-ink tracking-tight mb-4 border-b border-border/40 pb-2">
                      9. Governing Law
                    </h2>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-4">
                      These Terms of Use, their interpretation, and any dispute arising in connection with our services shall be governed exclusively by the laws of the <strong>Federal Republic of Nigeria</strong>.
                    </p>
                    <p className="text-[15.5px] text-ink/70 leading-relaxed mb-6">
                      Any legal suit, action, or proceeding arising out of or related to these Terms shall be instituted exclusively in the courts located in Lagos State, Nigeria.
                    </p>

                    <div className="rounded-3xl border border-border/60 bg-white p-6 shadow-sm">
                      <p className="font-display text-[16px] font-bold text-ink mb-1">
                        Legal Department (Hivebrain LTD)
                      </p>
                      <p className="text-[14.5px] text-ink/70 mb-3">
                        Lagos, Nigeria
                      </p>
                      <a
                        href="mailto:hello@telemedcare.app"
                        className="inline-flex items-center gap-2 text-forest hover:text-forestHover font-semibold text-[14.5px]"
                      >
                        <Mail className="h-4.5 w-4.5" />
                        hello@telemedcare.app
                      </a>
                    </div>
                  </section>

                </article>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
